import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Link,
  Badge,
  Button,
} from '@chakra-ui/react';
import { FaExclamationTriangle, FaCog } from 'react-icons/fa';
import dayjs from 'dayjs';
import LinkPaymentsModal from './LinkPayments/LinkPaymentsModal';

function getInitials(student) {
  const u = student?.user || {};
  const first = (u.first_name || '').trim().charAt(0) || '';
  const last = (u.last_name || '').trim().charAt(0) || '';
  return (first + last).toUpperCase() || '?';
}

function getFullName(student) {
  const u = student?.user || {};
  return `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—';
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD-MM-YYYY') : '—';
}

const STATUS_BADGE = {
  AL_DIA: { colorScheme: 'green', label: 'AL DÍA' },
  PROXIMO_A_VENCER: { colorScheme: 'orange', label: 'PRÓX. A VENCER' },
  EN_MORA: { colorScheme: 'red', label: 'EN MORA' },
  SIN_PAGOS: { colorScheme: 'gray', label: 'SIN PAGOS' },
  REVISION_PAUSA: { colorScheme: 'orange', label: 'REVISAR HISTORIAL DE PAUSA' },
};

/**
 * Tabla del reporte de cartera para programas is_semester_based (Opción B).
 * Estado financiero puramente calculado — sin override manual, sin menú de
 * acciones. Cindy corrige el estado ajustando las cantidades desde el modal
 * de vinculación manual.
 */
export default function CarteraTable({ data = [], onReload }) {
  // Modal de vinculación manual (PR-6).
  const [linkModalRow, setLinkModalRow] = useState(null);
  // Decremento optimista por enrollment (se limpia al cambiar `data`).
  const [localOrphanDelta, setLocalOrphanDelta] = useState({});
  useEffect(() => { setLocalOrphanDelta({}); }, [data]);

  const openLinkModal = useCallback((row) => setLinkModalRow(row), []);
  const closeLinkModal = useCallback(() => setLinkModalRow(null), []);
  const handleLinkSaved = useCallback((savedCount) => {
    if (!linkModalRow?.enrollmentId) return;
    // El delta local solo aplica cuando hubo vinculaciones nuevas (baja el contador
    // "N por vincular" al vuelo). El reload SIEMPRE corre — también refleja cambios
    // de ancla y desvinculaciones, que pasan savedCount=0.
    if (savedCount > 0) {
      setLocalOrphanDelta((prev) => ({
        ...prev,
        [linkModalRow.enrollmentId]: (prev[linkModalRow.enrollmentId] || 0) + savedCount,
      }));
    }
    if (onReload) setTimeout(() => onReload(), 900);
  }, [linkModalRow, onReload]);

  return (
    <>
      <TableContainer overflowX="auto" maxW="100%">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th
                position="sticky"
                left={0}
                zIndex={2}
                bg="gray.50"
                minW="280px"
                borderRightWidth="1px"
                py={3}
                px={4}
              >
                ESTUDIANTE
              </Th>
              <Th minW="180px" bg="gray.50" py={3}>ESTADO FINANCIERO</Th>
              <Th minW="140px" bg="gray.50" py={3}>MATERIAS</Th>
              <Th minW="120px" bg="gray.50" py={3}>INICIO</Th>
              <Th minW="120px" bg="gray.50" py={3}>ÚLT. PAGO</Th>
            </Tr>
          </Thead>
          <Tbody>
            {data.map((row) => {
              const status = row.financialStatus || 'SIN_PAGOS';
              const badge = STATUS_BADGE[status] || STATUS_BADGE.SIN_PAGOS;
              const rowBg = status === 'EN_MORA' ? 'red.50' : 'white';
              const rawOrphan = row.orphanPaymentsCount ?? 0;
              const localDelta = row.enrollmentId ? (localOrphanDelta[row.enrollmentId] || 0) : 0;
              const effectiveOrphan = Math.max(0, rawOrphan - localDelta);
              const canOpenModal = !!row.enrollmentId && !!row.enrollmentType;
              const hasPending = effectiveOrphan > 0;
              return (
                <Tr key={row.enrollmentId ?? `${row.student?.id}-${row.programId}`} bg={rowBg}>
                  <Td
                    position="sticky"
                    left={0}
                    zIndex={1}
                    bg={rowBg}
                    borderRightWidth="1px"
                    borderColor="gray.100"
                  >
                    <Flex align="center" gap={3}>
                      <Flex
                        w={10}
                        h={10}
                        borderRadius="full"
                        bg="#E8E4FC"
                        color="#574feb"
                        borderWidth="1px"
                        borderColor="#D4CCF0"
                        align="center"
                        justify="center"
                        fontSize="xs"
                        fontWeight="bold"
                      >
                        {getInitials(row.student)}
                      </Flex>
                      <Box>
                        <Link
                          href={`/plataforma/administrador/ver-estudiante/${row.student?.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          fontWeight="medium"
                          color="blue.600"
                          _hover={{ underline: true }}
                        >
                          {getFullName(row.student)}
                        </Link>
                        <Text fontSize="xs" color="gray.500">
                          ID: {row.student?.id ?? '—'}
                          {row.programName ? ` · ${row.programName}` : ''}
                        </Text>
                        {canOpenModal && (hasPending ? (
                          <Button
                            variant="link"
                            colorScheme="orange"
                            size="xs"
                            fontWeight="600"
                            leftIcon={<Box as={FaExclamationTriangle} boxSize="14px" />}
                            iconSpacing={1.5}
                            onClick={() => openLinkModal(row)}
                            mt={1}
                            h="auto"
                            minH="auto"
                            lineHeight="1"
                            alignItems="center"
                          >
                            {effectiveOrphan} por vincular
                          </Button>
                        ) : (
                          <Button
                            variant="link"
                            colorScheme="gray"
                            size="xs"
                            fontWeight="500"
                            leftIcon={<Box as={FaCog} boxSize="12px" />}
                            iconSpacing={1.5}
                            onClick={() => openLinkModal(row)}
                            mt={1}
                            h="auto"
                            minH="auto"
                            lineHeight="1"
                            alignItems="center"
                          >
                            Gestionar pagos
                          </Button>
                        ))}
                      </Box>
                    </Flex>
                  </Td>
                  <Td>
                    <Box display="inline-flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                      <Badge
                        colorScheme={badge.colorScheme}
                        fontSize="10px"
                        letterSpacing="0.03em"
                        px={2}
                        py={0.5}
                        borderRadius="4px"
                      >
                        {badge.label}
                      </Badge>
                      {status === 'EN_MORA' && row.overdueSince && (
                        <Text fontSize="11px" color="red.600" fontWeight="600">
                          desde {fmtDate(row.overdueSince)}
                        </Text>
                      )}
                      {status === 'REVISION_PAUSA' && <Text fontSize="11px" color="orange.700" whiteSpace="normal">Falta el historial necesario para calcular la cartera sin cobrar meses pausados.</Text>}
                    </Box>
                  </Td>
                  <Td>
                    <Box>
                      <Text fontSize="sm" fontWeight="700" color="gray.800" lineHeight="1.1">
                        {row.paidSubjectsCount ?? 0} pagadas
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {row.activatedSubjectsCount ?? 0} activadas
                      </Text>
                    </Box>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.700">
                      {fmtDate(row.startDate || row.firstPaymentDate)}
                    </Text>
                    {row.startDate && row.firstPaymentDate && fmtDate(row.startDate) !== fmtDate(row.firstPaymentDate) && (
                      <Text fontSize="11px" color="gray.500">
                        1er pago {fmtDate(row.firstPaymentDate)}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.700">
                      {fmtDate(row.lastPaymentDate)}
                    </Text>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Modal de vinculación manual (PR-6) */}
      <LinkPaymentsModal
        isOpen={!!linkModalRow}
        onClose={closeLinkModal}
        studentId={linkModalRow?.student?.id}
        programId={linkModalRow?.programId}
        enrollmentType={linkModalRow?.enrollmentType}
        enrollmentId={linkModalRow?.enrollmentId}
        studentName={linkModalRow ? getFullName(linkModalRow.student) : ''}
        programName={linkModalRow?.programName}
        semesterName={linkModalRow?.semesterName}
        anchorAuto={linkModalRow?.cobroAnchor}
        onSaved={handleLinkSaved}
      />
    </>
  );
}
